import { useEffect, useRef, useState } from "react";
import { Button, type ButtonProps, type ButtonVariant, type ButtonSize } from "./Button";
import { Icon } from "./Icon";

export interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "children"> {
  /** The string written to the clipboard. */
  value: string;
  /** Label while idle. Defaults to "Copy link". */
  label?: string;
  /** Label flashed for 1.5s after a successful copy. Defaults to "Copied!". */
  copiedLabel?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional callback fired with success/failure after the copy attempt. */
  onCopied?: (ok: boolean) => void;
}

/**
 * One-tap clipboard copy with confirmation feedback. Falls back to a hidden
 * textarea + document.execCommand when the Async Clipboard API is unavailable
 * (older browsers, non-secure contexts), so it works on the customer signing
 * surface where the secure-context guarantee can't be assumed for embeds.
 */
export function CopyButton({
  value,
  label = "Copy link",
  copiedLabel = "Copied!",
  variant = "secondary",
  size = "sm",
  onCopied,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  async function handleCopy() {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        ok = true;
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch {
      ok = false;
    }
    onCopied?.(ok);
    if (ok) {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      leadingIcon={<Icon name={copied ? "check" : "copy"} size={size === "sm" ? 14 : 16} />}
      aria-live="polite"
      {...rest}
    >
      {copied ? copiedLabel : label}
    </Button>
  );
}
